import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { inventoryService } from '../../../services/inventoryService';
import { countries } from '../../../utils/countries';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import { FiSearch } from 'react-icons/fi';

const formatInventoryLinkageDetails = (details) => {
  if (!details || typeof details !== 'object') {
    return '';
  }

  const labels = {
    inventoryStockRows: 'stock rows',
    inventoryAssetCount: 'inventory assets',
    requestLineItemCount: 'request line items',
    requestLineItemAssetCount: 'request line item assets',
    inboundProjectLineCount: 'manifest lines',
    inboundProjectReceiptLineCount: 'manifest receipt lines'
  };

  const activeLinks = Object.entries(details)
    .filter(([, count]) => Number(count) > 0)
    .map(([key, count]) => `${count} ${labels[key] || key}`);

  return activeLinks.length > 0 ? ` Linked records: ${activeLinks.join(', ')}.` : '';
};

const InventoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = !!id;
  
  // Form state
  const [formData, setFormData] = useState({
    CustomerId: '',
    productSku: '',
    internalSku: '',
    productName: '',
    packagingType: '',
    // Individual item dimensions/weight
    length: '',
    width: '',
    height: '',
    weightLbs: '',
    // Carton dimensions/weight
    cartonLength: '',
    cartonWidth: '',
    cartonHeight: '',
    cartonWeight: '',
    cartonUnitOfMeasurement: 'Each',
    unitsPerCarton: '',
    // Additional fields
    value: '',
    upc: '',
    alternatePartNumber: '',
    hsCode: '',
    countryOfOrigin: '',
    description: '',
    notes: '',
    // Image placeholder
    imageUrl: ''
  });
  
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Search customers with debouncing
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounced search function
  useEffect(() => {
    const searchCustomers = async () => {
      if (customerSearch.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await inventoryService.searchCustomers(customerSearch);
        setSearchResults(response?.data || []);
      } catch (error) {
        console.error('Error searching customers:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchCustomers, 300); // 300ms debounce
    return () => clearTimeout(timeoutId);
  }, [customerSearch]);

  // Use search results as filtered customers
  const filteredCustomers = searchResults;

  // Fetch existing inventory item if editing
  const { data: inventoryData, isLoading: inventoryLoading } = useQuery(
    ['inventory', id],
    () => inventoryService.getInventoryItem(id),
    {
      enabled: isEditMode,
      onSuccess: async (data) => {
        if (data?.data) {
          const item = data.data;
          setFormData({
            CustomerId: item.company_id || '',
            productSku: item.product_sku || '',
            internalSku: item.internal_sku || '',
            productName: item.product_name || '',
            packagingType: item.packaging_type || '',
            // Individual dimensions
            length: item.length || '',
            width: item.width || '',
            height: item.height || '',
            weightLbs: item.weight_lbs || '',
            // Carton dimensions
            cartonLength: item.carton_length || '',
            cartonWidth: item.carton_width || '',
            cartonHeight: item.carton_height || '',
            cartonWeight: item.carton_weight || '',
            cartonUnitOfMeasurement: item.carton_unit_of_measurement || 'Each',
            unitsPerCarton: item.units_per_carton || '',
            // Additional fields
            value: item.value || '',
            upc: item.upc || '',
            alternatePartNumber: item.alternate_part_number || '',
            hsCode: item.hs_code || '',
            countryOfOrigin: item.country_of_origin || '',
            description: item.description || '',
            notes: item.notes || '',
            imageUrl: item.image_url || ''
          });
          
          // Set customer search to show selected customer
          if (item.company_id) {
            // For edit mode, we need to fetch the customer name
            try {
              const response = await inventoryService.searchCustomers(''); // Get some customers
              const customer = response?.data?.find(c => c.id === item.company_id);
              if (customer) {
                setCustomerSearch(customer.company_name);
              } else {
                // Fallback: try to get customer name from the item data if available
                setCustomerSearch(item.Company?.name || 'Unknown Customer');
              }
            } catch (error) {
              console.error('Error loading customer for edit:', error);
              setCustomerSearch('Unknown Customer');
            }
          }
        }
      }
    }
  );

  // Create/Update mutation
  const mutation = useMutation(
    (data) => {
      if (isEditMode) {
        return inventoryService.updateInventoryItem(id, data);
      } else {
        return inventoryService.createInventoryItem(data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['inventory']);
        navigate('/admin/inventory');
      },
      onError: (error) => {
        const message = error.response?.data?.message || error.message || 'An error occurred while saving the inventory item';
        const details = formatInventoryLinkageDetails(error.response?.data?.details);
        setError(`${message}${details}`);
      }
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      CustomerId: customer.id
    }));
    setCustomerSearch(customer.company_name);
    setShowCustomerDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      // Validate required fields
      if (!formData.CustomerId) {
        throw new Error('Please select a customer');
      }
      if (!formData.packagingType) {
        throw new Error('Please select a packaging type');
      }
      
      // Validate dimensional fields based on packaging type
      if (formData.packagingType === 'individual') {
        if (!formData.length || !formData.width || !formData.height || !formData.weightLbs) {
          throw new Error('Individual item dimensions and weight are required');
        }
      } else if (formData.packagingType === 'carton') {
        if (!formData.cartonLength || !formData.cartonWidth || !formData.cartonHeight || !formData.cartonWeight) {
          throw new Error('Carton dimensions and weight are required');
        }
        if (!formData.cartonUnitOfMeasurement) {
          throw new Error('Carton unit of measurement is required');
        }
        if (!formData.length || !formData.width || !formData.height || !formData.weightLbs) {
          throw new Error('Individual item dimensions and weight are required');
        }
        if (!formData.unitsPerCarton) {
          throw new Error('Units per carton is required');
        }
      }

      // Prepare data for submission
      const submitData = {
        company_id: formData.CustomerId,
        product_sku: formData.productSku,
        internal_sku: formData.internalSku || null,
        product_name: formData.productName,
        packaging_type: formData.packagingType,
        // Individual dimensions
        length: parseFloat(formData.length) || null,
        width: parseFloat(formData.width) || null,
        height: parseFloat(formData.height) || null,
        weight_lbs: parseFloat(formData.weightLbs) || null,
        // Carton dimensions
        carton_length: parseFloat(formData.cartonLength) || null,
        carton_width: parseFloat(formData.cartonWidth) || null,
        carton_height: parseFloat(formData.cartonHeight) || null,
        carton_weight: parseFloat(formData.cartonWeight) || null,
        carton_unit_of_measurement: formData.cartonUnitOfMeasurement || null,
        units_per_carton: parseInt(formData.unitsPerCarton) || null,
        // Additional fields
        value: parseFloat(formData.value) || null,
        upc: formData.upc || null,
        alternate_part_number: formData.alternatePartNumber || null,
        hs_code: formData.hsCode || null,
        country_of_origin: formData.countryOfOrigin || null,
        description: formData.description || null,
        notes: formData.notes || null,
        image_url: formData.imageUrl || null // TODO: Implement actual image upload
      };

      await mutation.mutateAsync(submitData);
    } catch (error) {
      // Handle SKU validation errors specifically
      if (error.response?.data?.field === 'product_sku') {
        const details = formatInventoryLinkageDetails(error.response?.data?.details);
        setError(`${error.response.data.message}${details}`);
      } else {
        const message = error.response?.data?.message || error.message || 'An error occurred';
        const details = formatInventoryLinkageDetails(error.response?.data?.details);
        setError(`${message}${details}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (inventoryLoading) return <LoadingSpinner />;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">
          {isEditMode ? 'Edit Inventory Item' : 'Add New Inventory Item'}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Add a new product to your inventory catalog
        </p>
      </div>

      {error && (
        <div className="mb-6">
          <ErrorAlert message={error} />
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Customer Selection */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Selection</h3>
          
          <div className="relative">
            <label htmlFor="customer" className="block text-sm font-medium text-gray-700 mb-2">
              Customer *
            </label>
            <div className="relative">
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  setShowCustomerDropdown(true);
                }}
                onFocus={() => setShowCustomerDropdown(true)}
                placeholder="Search customers..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <FiSearch className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {showCustomerDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {isSearching ? (
                  <div className="p-4 text-center text-gray-500">Searching customers...</div>
                ) : customerSearch.length < 2 ? (
                  <div className="p-4 text-center text-gray-500">Type at least 2 characters to search</div>
                ) : filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => handleCustomerSelect(customer)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">{customer.company_name}</div>
                      <div className="text-sm text-gray-500">{customer.email}</div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500">No customers found</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Basic Product Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Product Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="productSku" className="block text-sm font-medium text-gray-700 mb-2">
                Product SKU *
              </label>
              <input
                type="text"
                id="productSku"
                name="productSku"
                value={formData.productSku}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Customer's SKU"
              />
            </div>

            <div>
              <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                id="productName"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter product name"
              />
            </div>
            <div>
              <label htmlFor="internalSku" className="block text-sm font-medium text-gray-700 mb-2">
                Internal SKU (Optional)
              </label>
              <input
                type="text"
                id="internalSku"
                name="internalSku"
                value={formData.internalSku}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Internal part number"
              />
            </div>
            <div>
              <label htmlFor="alternatePartNumber" className="block text-sm font-medium text-gray-700 mb-2">
                Manufacturer SKU (Optional)
              </label>
              <input
                type="text"
                id="alternatePartNumber"
                name="alternatePartNumber"
                value={formData.alternatePartNumber}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Manufacturer part number"
              />
            </div>
          </div>
        </div>

        {/* Packaging Type */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Packaging Information</h3>
          
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="packagingType" className="block text-sm font-medium text-gray-700 mb-2">
                Packaging Type *
              </label>
              <select
                id="packagingType"
                name="packagingType"
                value={formData.packagingType}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Packaging Type</option>
                <option value="individual">Individual Units</option>
                <option value="carton">Carton</option>
              </select>
            </div>
            <div>
              <label htmlFor="unitOfMeasurement" className="block text-sm font-medium text-gray-700 mb-2">
                Unit of Measurement
              </label>
              <select
                id="unitOfMeasurement"
                name="unitOfMeasurement"
                value={formData.unitOfMeasurement}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Each">Each</option>
                <option value="Box">Box</option>
                <option value="Pallet">Pallet</option>
              </select>
            </div>
          </div>

          {formData.packagingType && (
            <>
              <div className="mb-6">
                <label htmlFor="weightLbs" className="block text-sm font-medium text-gray-700 mb-2">
                  Item Weight
                </label>
                <input
                  type="number"
                  id="weightLbs"
                  name="weightLbs"
                  value={formData.weightLbs}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  required={formData.packagingType}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Item Dimensions (inches)</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="length" className="block text-sm font-medium text-gray-700 mb-1">
                    Length
                  </label>
                  <input
                    type="number"
                    id="length"
                    name="length"
                    value={formData.length}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    required={formData.packagingType}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="width" className="block text-sm font-medium text-gray-700 mb-1">
                    Width
                  </label>
                  <input
                    type="number"
                    id="width"
                    name="width"
                    value={formData.width}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    required={formData.packagingType}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="height" className="block text-sm font-medium text-gray-700 mb-1">
                    Height
                  </label>
                  <input
                    type="number"
                    id="height"
                    name="height"
                    value={formData.height}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    required={formData.packagingType}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              </div>
            </>
          )}

          {formData.packagingType === 'carton' && (
            <>
              <div className="mb-6">
                <label htmlFor="cartonWeight" className="block text-sm font-medium text-gray-700 mb-2">
                  Carton Weight
                </label>
                <input
                  type="number"
                  id="cartonWeight"
                  name="cartonWeight"
                  value={formData.cartonWeight}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Carton Dimensions</label>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label htmlFor="cartonLength" className="block text-sm font-medium text-gray-700 mb-1">
                    Length
                  </label>
                  <input
                    type="number"
                    id="cartonLength"
                    name="cartonLength"
                    value={formData.cartonLength}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="cartonWidth" className="block text-sm font-medium text-gray-700 mb-1">
                    Width
                  </label>
                  <input
                    type="number"
                    id="cartonWidth"
                    name="cartonWidth"
                    value={formData.cartonWidth}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="cartonHeight" className="block text-sm font-medium text-gray-700 mb-1">
                    Height
                  </label>
                  <input
                    type="number"
                    id="cartonHeight"
                    name="cartonHeight"
                    value={formData.cartonHeight}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="cartonUnitOfMeasurement" className="block text-sm font-medium text-gray-700 mb-1">
                    Carton UOM
                  </label>
                  <select
                    id="cartonUnitOfMeasurement"
                    name="cartonUnitOfMeasurement"
                    value={formData.cartonUnitOfMeasurement}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Each">Each</option>
                    <option value="Box">Box</option>
                    <option value="Case">Case</option>
                    <option value="Pack">Pack</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="unitsPerCarton" className="block text-sm font-medium text-gray-700 mb-1">
                    Units per Carton
                  </label>
                  <input
                    type="number"
                    id="unitsPerCarton"
                    name="unitsPerCarton"
                    value={formData.unitsPerCarton}
                    onChange={handleChange}
                    min="1"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              </div>
            </>
          )}
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="value" className="block text-sm font-medium text-gray-700 mb-2">
                Approximate Value ($)
              </label>
              <input
                type="number"
                id="value"
                name="value"
                value={formData.value}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
            
              <div>
                <label htmlFor="upc" className="block text-sm font-medium text-gray-700 mb-2">
                  UPC / EAN / GTIN
              </label>
              <input
                type="text"
                id="upc"
                name="upc"
                value={formData.upc}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter barcode"
              />
            </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="hsCode" className="block text-sm font-medium text-gray-700 mb-2">
                HS Code
              </label>
              <input
                type="text"
                id="hsCode"
                name="hsCode"
                value={formData.hsCode}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Harmonized System code"
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="countryOfOrigin" className="block text-sm font-medium text-gray-700 mb-2">
              Country of Origin
            </label>
            <select
              id="countryOfOrigin"
              name="countryOfOrigin"
              value={formData.countryOfOrigin}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Country</option>
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Description & Notes</h3>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Product Description (Optional)
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Product description..."
              />
            </div>
            
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Internal Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Internal notes (not visible to customers)..."
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pb-8">
          <button
            type="button"
            onClick={() => navigate('/admin/inventory')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : (isEditMode ? 'Update Item' : 'Create Item')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InventoryForm; 
